import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { ZoomMtg } from "@zoom/meetingsdk";
import "./App.css";
// Initialize SDK
ZoomMtg.preLoadWasm();
ZoomMtg.prepareWebSDK();
const Zoom = () => {
    // Add state for dynamic meeting data
    const [meetingData, setMeetingData] = useState(null);
    const [, setError] = useState(null);
    const [studentDetails, setStudentDetails] = useState(null);
    const [, setClassDetails] = useState(null);
    // Get class_id from URL params for back button
    const [classId] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get('class_id');
    });
    // Configuration - use environment variables or props in production
    const authEndpoint = "https://admin.eoe.lk/api/zoom/public-signature"; // Use public endpoint
    const sdkKey = "vseB0RJQpSAwfsnd7FsQ";
    const role = 0; // 0 for attendee, 1 for host
    const userName = studentDetails?.first_name ? `${studentDetails.first_name} ${studentDetails.last_name}` : "Guest User";
    const userEmail = studentDetails?.email || "";
    const registrantToken = "";
    const zakToken = "";
    const leaveUrl = window.location.href;
    const healthEndpoint = "https://admin.eoe.lk/api/zoom/health-check";
    // Function to parse URL parameters and extract meeting data
    const parseUrlParams = async () => {
        const urlParams = new URLSearchParams(window.location.search);
        const meetingId = urlParams.get('meetingId');
        const password = urlParams.get('password');
        const token = urlParams.get('token');
        const videoId = urlParams.get('videoId'); // New parameter for class video ID
        // If video ID is provided, fetch zoom details from backend
        if (videoId && token) {
            try {
                console.log('Fetching zoom details for video ID:', videoId);
                const response = await fetch(`https://admin.eoe.lk/api/get_zoom_details/${videoId}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
                if (response.ok) {
                    const result = await response.json();
                    if (result.success && result.data) {
                        // Also fetch student details
                        const studentResponse = await fetch(`https://admin.eoe.lk/api/student/details`, {
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                            }
                        });
                        if (studentResponse.ok) {
                            const studentData = await studentResponse.json();
                            if (studentData.success && studentData.data) {
                                setStudentDetails(studentData.data);
                            }
                            setClassDetails({
                                name: result.data.name,
                                subject: result.data.subject || 'N/A',
                                teacher: result.data.teacher || 'N/A',
                                startTime: new Date().toLocaleTimeString(),
                                duration: '2 hours'
                            });
                        }
                        setMeetingData({
                            meetingNumber: result.data.zoom_id,
                            passWord: result.data.zoom_password,
                            meetingName: result.data.name || `Meeting ${result.data.zoom_id}`,
                            token: token
                        });
                        console.log('Zoom details fetched successfully:', result.data);
                        return true;
                    }
                    else {
                        setError('Failed to fetch zoom meeting details: ' + (result.message || 'Unknown error'));
                        return false;
                    }
                }
                else {
                    setError('Failed to connect to server to fetch zoom details');
                    return false;
                }
            }
            catch (error) {
                console.error('Error fetching zoom details:', error);
                setError('Error fetching zoom details: ' + (error instanceof Error ? error.message : 'Unknown error'));
                return false;
            }
        }
        // Fallback to direct URL parameters
        else if (meetingId && password && token) {
            setMeetingData({
                meetingNumber: meetingId,
                passWord: password,
                meetingName: `Meeting ${meetingId}`,
                token: token
            });
            console.log('Meeting data extracted from URL:', { meetingId, password, token });
            return true;
        }
        return false;
    };
    // Auto-parse URL parameters on component mount
    useEffect(() => {
        const initializeMeetingData = async () => {
            const hasUrlParams = await parseUrlParams();
            if (!hasUrlParams) {
                setError('Missing required URL parameters: meetingId, password, token, or videoId');
            }
        };
        initializeMeetingData();
    }, []);
    // Force and maintain "Everest Zoom" title throughout the component lifecycle
    useEffect(() => {
        // Set title immediately
        document.title = "Everest Zoom";
        // Override document.title property to prevent any changes
        const originalTitleDescriptor = Object.getOwnPropertyDescriptor(Document.prototype, 'title');
        Object.defineProperty(document, 'title', {
            get: function () {
                return "Everest Zoom";
            },
            set: function (_value) {
                // Intercept all attempts to change title and do nothing
                // Always keep it as "Everest Zoom"
                const titleElement = document.querySelector('title');
                if (titleElement) {
                    titleElement.textContent = "Everest Zoom";
                }
            },
            configurable: true
        });
        // Also monitor and reset title if it changes through DOM manipulation
        const titleObserver = setInterval(() => {
            const titleElement = document.querySelector('title');
            if (titleElement && titleElement.textContent !== "Everest Zoom") {
                titleElement.textContent = "Everest Zoom";
            }
        }, 50);
        // Use MutationObserver to catch title changes immediately
        const observer = new MutationObserver(() => {
            const titleElement = document.querySelector('title');
            if (titleElement && titleElement.textContent !== "Everest Zoom") {
                titleElement.textContent = "Everest Zoom";
            }
        });
        // Observe the title element
        const titleElement = document.querySelector('title');
        if (titleElement) {
            observer.observe(titleElement, {
                childList: true,
                characterData: true,
                subtree: true
            });
        }
        // Cleanup on unmount
        return () => {
            clearInterval(titleObserver);
            observer.disconnect();
            // Restore original title descriptor
            if (originalTitleDescriptor) {
                Object.defineProperty(document, 'title', originalTitleDescriptor);
            }
        };
    }, []);
    // Handle window resize to adjust Zoom video container
    useEffect(() => {
        let resizeTimeout;
        const forceZoomResize = () => {
            const zmmtgRoot = document.getElementById('zmmtg-root');
            if (!zmmtgRoot || zmmtgRoot.style.display === 'none')
                return;
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            const videoAreaHeight = viewportHeight - 96; // Account for header/footer
            // Force root container
            zmmtgRoot.style.width = `${viewportWidth}px`;
            zmmtgRoot.style.height = `${viewportHeight}px`;
            // Update video-share-layout
            const videoShareLayout = zmmtgRoot.querySelector('#video-share-layout');
            if (videoShareLayout) {
                videoShareLayout.style.width = `${viewportWidth}px`;
                videoShareLayout.style.height = `${videoAreaHeight}px`;
            }
            // Update main layout
            const mainLayout = zmmtgRoot.querySelector('.main-layout');
            if (mainLayout) {
                mainLayout.style.width = `${viewportWidth}px`;
                mainLayout.style.height = `${videoAreaHeight}px`;
            }
            // Update video-player-container elements
            zmmtgRoot.querySelectorAll('video-player-container').forEach(el => {
                const htmlEl = el;
                if (!htmlEl.closest('.suspension-window-container')) {
                    htmlEl.style.width = `${viewportWidth}px`;
                    htmlEl.style.height = `${videoAreaHeight}px`;
                }
            });
            // Update single-main-container__video-frame
            const videoFrame = zmmtgRoot.querySelector('.single-main-container__video-frame');
            if (videoFrame) {
                videoFrame.style.width = `${viewportWidth}px`;
                videoFrame.style.height = `${videoAreaHeight}px`;
            }
            // Update video-player elements
            zmmtgRoot.querySelectorAll('video-player').forEach(el => {
                const htmlEl = el;
                if (!htmlEl.closest('.suspension-window-container')) {
                    htmlEl.style.width = `${viewportWidth}px`;
                    htmlEl.style.height = `${videoAreaHeight}px`;
                }
            });
            // Update canvas elements inside video-player-container
            zmmtgRoot.querySelectorAll('video-player-container canvas').forEach(el => {
                const htmlEl = el;
                if (!htmlEl.closest('.suspension-window-container')) {
                    htmlEl.style.width = `${viewportWidth}px`;
                    htmlEl.style.height = `${videoAreaHeight}px`;
                }
            });
            // Update Pane2 (main video pane)
            const pane2 = zmmtgRoot.querySelector('.Pane.horizontal.Pane2');
            if (pane2) {
                pane2.style.height = `${videoAreaHeight}px`;
            }
        };
        const handleResize = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                forceZoomResize();
                setTimeout(forceZoomResize, 200);
                setTimeout(forceZoomResize, 500);
            }, 50);
        };
        window.addEventListener('resize', handleResize);
        // Handle visibility change (minimize/maximize)
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                setTimeout(forceZoomResize, 100);
                setTimeout(forceZoomResize, 300);
                setTimeout(forceZoomResize, 600);
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            window.removeEventListener('resize', handleResize);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            clearTimeout(resizeTimeout);
        };
    }, []);
    // Block camera access in the browser for attendees
    useEffect(() => {
        if (role === 1)
            return;
        const mediaDevices = navigator.mediaDevices;
        if (!mediaDevices || typeof mediaDevices.getUserMedia !== "function")
            return;
        const originalGetUserMedia = mediaDevices.getUserMedia.bind(mediaDevices);
        const legacyGetUserMedia = navigator.getUserMedia
            ? navigator.getUserMedia.bind(navigator)
            : null;
        mediaDevices.getUserMedia = (constraints) => {
            const wantsVideo = typeof constraints === "object" &&
                constraints !== null &&
                constraints.video;
            if (wantsVideo) {
                return Promise.reject(new DOMException("Camera access blocked by policy", "NotAllowedError"));
            }
            return originalGetUserMedia(constraints);
        };
        if (legacyGetUserMedia) {
            navigator.getUserMedia = (constraints, success, failure) => {
                const wantsVideo = typeof constraints === "object" &&
                    constraints !== null &&
                    constraints.video;
                if (wantsVideo) {
                    if (failure) {
                        failure(new DOMException("Camera access blocked by policy", "NotAllowedError"));
                    }
                    return;
                }
                return legacyGetUserMedia(constraints, success, failure);
            };
        }
        return () => {
            mediaDevices.getUserMedia = originalGetUserMedia;
            if (legacyGetUserMedia) {
                navigator.getUserMedia = legacyGetUserMedia;
            }
        };
    }, [role]);
    // Clean up the Zoom UI when component unmounts
    useEffect(() => {
        if (role !== 1) {
            const isEditableTarget = (el) => {
                if (!(el instanceof HTMLElement))
                    return false;
                const tag = el.tagName.toLowerCase();
                if (tag === 'input' || tag === 'textarea')
                    return true;
                if (el.isContentEditable)
                    return true;
                // Zoom chat often uses ARIA textbox/contentEditable wrappers
                if (el.getAttribute('role') === 'textbox')
                    return true;
                if (el.closest('[contenteditable="true"]'))
                    return true;
                // Fallback: anything inside Zoom chat composer
                if (el.closest('#zmmtg-root .chat-box__chat-textarea, #zmmtg-root .chat-box__chat-textarea input, #zmmtg-root .chat-box__chat-textarea textarea'))
                    return true;
                return false;
            };
            const blockKeyboardShortcuts = (event) => {
                const inEditable = isEditableTarget(event.target);
                // --- Allow normal typing in chat/inputs ---
                if (inEditable) {
                    // Still block dangerous *shortcuts* inside inputs:
                    if (event.altKey) {
                        event.preventDefault();
                        event.stopPropagation();
                        return false;
                    }
                    if (event.ctrlKey || event.metaKey) {
                        const k = event.key.toLowerCase();
                        // Allow only common editing combos; block the rest
                        if (!['c', 'v', 'x', 'a', 'z', 'y'].includes(k)) {
                            event.preventDefault();
                            event.stopPropagation();
                            return false;
                        }
                        return true;
                    }
                    // No modifiers → let the keystroke through (typing, arrows, backspace, enter, etc.)
                    return true;
                }
                // --- Not in an input/chat box: block shortcuts like before ---
                const k = event.key.toLowerCase();
                // Block all Alt combinations
                if (event.altKey) {
                    event.preventDefault();
                    event.stopPropagation();
                    return false;
                }
                // Block Shift + hotkeys commonly used by Zoom
                if (event.shiftKey && ['a', 'v', 's', 'h', 'w', 'r'].includes(k)) {
                    event.preventDefault();
                    event.stopPropagation();
                    return false;
                }
                // Block function keys F1–F24
                if (/^f\d{1,2}$/i.test(event.key)) {
                    event.preventDefault();
                    event.stopPropagation();
                    return false;
                }
                // Allow basic editing shortcuts; block other Ctrl/Meta combos
                if (event.ctrlKey || event.metaKey) {
                    if (!['c', 'v', 'x', 'a'].includes(k)) {
                        event.preventDefault();
                        event.stopPropagation();
                        return false;
                    }
                    return true;
                }
                // Block common Zoom single-key triggers when no modifiers
                const blockedKeys = ['a', 'v', 's', 'h', 'w', 'r', 'q', 'e', 't', 'y', 'u', 'i', 'o', 'p'];
                if (blockedKeys.includes(k)) {
                    event.preventDefault();
                    event.stopPropagation();
                    return false;
                }
                return true;
            };
            const blockContextMenu = (event) => {
                // Optional: allow right-click inside chat input if you want paste menu
                // if (isEditableTarget(event.target)) return; // uncomment to allow in chat
                event.preventDefault();
                return false;
            };
            document.addEventListener('keydown', blockKeyboardShortcuts, true);
            document.addEventListener('contextmenu', blockContextMenu, true);
            return () => {
                document.removeEventListener('keydown', blockKeyboardShortcuts, true);
                document.removeEventListener('contextmenu', blockContextMenu, true);
                const zmmtgRoot = document.getElementById('zmmtg-root');
                if (zmmtgRoot)
                    zmmtgRoot.style.display = 'none';
            };
        }
        else {
            return () => {
                const zmmtgRoot = document.getElementById('zmmtg-root');
                if (zmmtgRoot)
                    zmmtgRoot.style.display = 'none';
            };
        }
    }, [role]);
    const sendHealthPing = async () => {
        try {
            if (!meetingData?.token)
                return;
            const urlParams = new URLSearchParams(window.location.search);
            const resp = await fetch(healthEndpoint, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${meetingData.token}`,
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                },
                body: JSON.stringify({
                    meetingNumber: meetingData.meetingNumber || null,
                    videoId: urlParams.get("videoId") || null,
                    ts: Date.now(),
                    userName: userName,
                }),
            });
            if (resp.status === 401) {
                ZoomMtg.leaveMeeting({});
                alert("Multiple sessions detected. You will be redirected.");
                window.location.href = leaveUrl;
                return;
            }
            if (!resp.ok) {
                console.warn("Health ping failed:", resp.status);
            }
        }
        catch (e) {
            console.warn("Health ping failed:", e);
        }
    };
    useEffect(() => {
        if (!meetingData)
            return;
        sendHealthPing();
        const id = setInterval(sendHealthPing, 60000);
        const onVis = () => {
            if (document.visibilityState === "visible")
                sendHealthPing();
        };
        document.addEventListener("visibilitychange", onVis);
        return () => {
            clearInterval(id);
            document.removeEventListener("visibilitychange", onVis);
        };
    }, [meetingData]);
    function startMeeting(signature) {
        if (!meetingData) {
            alert('No meeting data available. Please check URL parameters.');
            return;
        }
        console.log("Starting meeting...");
        // Force title to "Everest Zoom" immediately when meeting starts loading
        document.title = "Everest Zoom";
        // Show the Zoom container
        const zmmtgRoot = document.getElementById("zmmtg-root");
        if (zmmtgRoot) {
            zmmtgRoot.style.display = "block";
        }
        const meetingNumber = meetingData.meetingNumber;
        const meetingPassword = meetingData.passWord;
        const meetingUserName = userName || 'TestUser';
        const meetingSdkKey = sdkKey;
        const meetingSignature = signature;
        ZoomMtg.init({
            leaveUrl: leaveUrl,
            patchJsMedia: true,
            success: (success) => {
                console.log("Init success:", success);
                // Force the title to remain "Everest Zoom"
                document.title = "Everest Zoom";
                // Keep monitoring and resetting the title if Zoom SDK changes it
                setInterval(() => {
                    if (document.title !== "Everest Zoom") {
                        document.title = "Everest Zoom";
                    }
                }, 100);
                // Create a properly typed join configuration
                const joinConfig = {
                    signature: meetingSignature,
                    sdkKey: meetingSdkKey,
                    meetingNumber: meetingNumber,
                    passWord: meetingPassword,
                    userName: meetingUserName,
                    success: (joinSuccess) => {
                        console.log("Join success:", joinSuccess);
                        // Force the title to "Everest Zoom" when meeting starts
                        document.title = "Everest Zoom";
                        // For attendees, disable annotations and screen sharing
                        if (role !== 1) {
                            setTimeout(() => {
                                try {
                                    // Check if methods exist before calling them
                                    if (ZoomMtg.inMeetingService && typeof ZoomMtg.inMeetingService.disableAnnotation === 'function') {
                                        ZoomMtg.inMeetingService.disableAnnotation();
                                        console.log("Annotation disabled successfully");
                                    }
                                    // Try to disable screen sharing
                                    if (ZoomMtg.inMeetingService && typeof ZoomMtg.inMeetingService.updateSharingStatus === 'function') {
                                        ZoomMtg.inMeetingService.updateSharingStatus({
                                            shareStatus: false
                                        });
                                    }
                                    else if (ZoomMtg.sharing && typeof ZoomMtg.sharing.disableScreenShare === 'function') {
                                        ZoomMtg.sharing.disableScreenShare();
                                    }
                                }
                                catch (error) {
                                    console.log("Error applying attendee restrictions:", error);
                                }
                            }, 2000);
                        }
                    },
                    error: (error) => {
                        console.error("Join error:", error);
                        if (typeof error === 'object' && error !== null) {
                            console.log("Error code:", error.errorCode);
                            console.log("Error message:", error.errorMessage);
                            let errorMessage = "Failed to join meeting";
                            switch (error.errorCode) {
                                case 3706:
                                    errorMessage = "The meeting number is wrong or the meeting hasn't started yet.";
                                    break;
                                case 3707:
                                    errorMessage = "The meeting has expired or been ended by the host.";
                                    break;
                                case 3708:
                                    errorMessage = "The meeting is locked by the host.";
                                    break;
                                case 3711:
                                    errorMessage = "Invalid meeting password.";
                                    break;
                                case 1:
                                    errorMessage = "User cancelled or connection failed.";
                                    break;
                                default:
                                    errorMessage = error?.errorMessage || 'Unknown error occurred';
                            }
                            alert(errorMessage);
                            // Hide Zoom container on error
                            const zmmtgRoot = document.getElementById("zmmtg-root");
                            if (zmmtgRoot) {
                                zmmtgRoot.style.display = "none";
                            }
                        }
                    }
                };
                // Only add optional parameters if they exist and are not empty
                if (userEmail && userEmail.trim()) {
                    joinConfig.userEmail = userEmail;
                }
                if (registrantToken && registrantToken.trim()) {
                    joinConfig.tk = registrantToken;
                }
                if (zakToken && zakToken.trim()) {
                    joinConfig.zak = zakToken;
                }
                console.log("Join parameters:", joinConfig);
                // Join the meeting
                ZoomMtg.join(joinConfig);
                // Set up event listeners
                try {
                    // Listen for user join events
                    ZoomMtg.inMeetingServiceListener("onUserJoin", function (data) {
                        console.log("User joined:", data);
                    });
                    // Listen for meeting status changes
                    ZoomMtg.inMeetingServiceListener("onMeetingStatus", function (data) {
                        console.log("Meeting status changed:", data);
                    });
                }
                catch (error) {
                    console.log("Error setting up event listeners:", error);
                }
            },
            error: (error) => {
                console.error("Init error:", error);
                alert(`Failed to initialize Zoom: ${error?.errorMessage || 'Unknown error'}`);
                // Hide Zoom container on error
                const zmmtgRoot = document.getElementById("zmmtg-root");
                if (zmmtgRoot) {
                    zmmtgRoot.style.display = "none";
                }
            },
        });
    }
    // Enhanced signature request with token authentication
    const getSignature = async () => {
        if (!meetingData) {
            alert('No meeting data available. Please check URL parameters.');
            return;
        }
        // Set title immediately when user clicks Join Meeting
        document.title = "Everest Zoom";
        try {
            console.log("Requesting signature for meeting:", meetingData.meetingNumber);
            // Check if essential elements exist first
            if (!sdkKey || !meetingData.meetingNumber) {
                throw new Error("Missing required parameters: sdkKey or meetingNumber");
            }
            // Set up request headers - no auth token needed for public endpoint
            const headers = {
                "Content-Type": "application/json",
                "Accept": "application/json"
            };
            console.log("Sending request to:", authEndpoint);
            const req = await fetch(authEndpoint, {
                method: "POST",
                headers: headers,
                body: JSON.stringify({
                    meetingNumber: meetingData.meetingNumber,
                    role: role,
                    sdkKey: sdkKey
                }),
            });
            if (!req.ok) {
                const errorText = await req.text();
                console.error("Signature request failed:", req.status, errorText);
                throw new Error(`HTTP error! status: ${req.status}, message: ${errorText}`);
            }
            const res = await req.json();
            console.log("Signature response:", res);
            if (res.signature) {
                const signature = res.signature.trim();
                console.log("Signature received successfully");
                startMeeting(signature);
            }
            else {
                console.error("No signature in response:", res);
                alert("Failed to get signature from authorization server");
            }
        }
        catch (e) {
            console.error("Error getting signature:", e);
            const errorMessage = e instanceof Error ? e.message : 'Unknown error';
            alert(`Failed to get signature: ${errorMessage}`);
        }
    };
    return (_jsx("div", { className: "App", style: { position: 'relative', zIndex: 10000 }, children: _jsxs("main", { children: [classId && _jsx("div", { style: { marginBottom: '16px', textAlign: 'left' }, children: _jsx("a", { href: `https://dev4.eoe.lk/singleclass/${classId}`, style: {
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 16px',
                        backgroundColor: '#f0f0f0',
                        color: '#333',
                        textDecoration: 'none',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: 500,
                        border: '1px solid #ddd',
                        cursor: 'pointer',
                    }, children: "\u2190 Back" }) }), studentDetails && (_jsxs("div", { style: {
                        marginBottom: '20px',
                        padding: '16px',
                        border: '1px solid #eee',
                        borderRadius: '8px',
                        background: '#fff8e1',
                    }, children: [_jsx("h3", { style: { color: '#2c3e50', marginBottom: '15px' }, children: "Student Information" }), _jsxs("div", { children: [_jsx("strong", { children: "Last Name:" }), " ", studentDetails.last_name] })] })), meetingData && (_jsx("button", { onClick: getSignature, style: {
                        padding: '10px 20px',
                        cursor: 'pointer',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        fontSize: '16px'
                    }, children: "Join Meeting" })), !meetingData && (_jsxs("div", { style: {
                        marginBottom: '20px',
                        padding: '16px',
                        border: '1px solid #eee',
                        borderRadius: '8px',
                        background: '#fff8e1',
                    }, children: [_jsx("h3", { style: { margin: 0, color: '#7a5b00' }, children: "No Zoom classes right now" }), _jsx("p", { style: { margin: '8px 0 0', color: '#5f4a00' }, children: "There are currently no active Zoom sessions. Please contact your admins for assistance." })] })), _jsx("div", { style: { marginTop: '20px', padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '5px' } })] }) }));
};
export default Zoom;
